import { ILoggedInUser } from "../apis/auth/auth.interfaces";
import { TInstitute } from "../apis/institute/institute.interfaces";
import { isInstituteScopedUser } from "./userTypes";

/**
 * Gets the institute ID that should be used for API requests
 * For restricted users, returns their assigned institute ID
 * For standard users, returns the selected institute ID from context (if available)
 *
 * @param userDetails - Current logged in user details
 * @param selectedInstitute - Currently selected institute from context
 * @returns Institute ID string or undefined
 */
export const getInstituteIdForRequest = (
  userDetails: ILoggedInUser | null,
  selectedInstitute: TInstitute | null,
): string | undefined => {
  if (!userDetails) {
    return undefined;
  }

  // For restricted users, always use their assigned institute
  if (isInstituteScopedUser(userDetails)) {
    return userDetails.assignedInstituteId || userDetails.assignedInstitute?.id;
  }

  // For standard users, use the selected institute from context
  return selectedInstitute?.id;
};

/**
 * Adds institute ID to request params if user is restricted type
 *
 * @param params - Existing request params object
 * @param userDetails - Current logged in user details
 * @param selectedInstitute - Currently selected institute from context
 * @returns Params object with instituteId added if needed
 */
export const addInstituteIdToParams = <T extends Record<string, any>>(
  params: T | undefined,
  userDetails: ILoggedInUser | null,
  selectedInstitute: TInstitute | null,
): T & { instituteId?: string } => {
  const instituteId = getInstituteIdForRequest(userDetails, selectedInstitute);

  if (!instituteId) {
    return params || ({} as T);
  }

  return {
    ...params,
    instituteId,
  } as T & { instituteId: string };
};
